import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import { Store } from '@ngxs/store';
import {LenderUser} from 'app/models/dto/lender-user.model';
import { SessionService } from 'app/modules/session/services/session.service';
import {
  GoOffline,
  GoOfflineSubscription,
  GoOnline,
  GoOnlineSubscription
} from 'app/store/lender-user/lender-user/lender-user.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { environment } from 'environments/environment';
import {Observable, Subject} from 'rxjs';
import { toZonedTime } from 'date-fns-tz';
import {take, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  standalone: false
})
export class UserMenuComponent implements OnInit, OnDestroy {

  onlineStatus$: Observable<boolean> = this.store.select(LenderUserState.online);
  @Input() user: LenderUser;

  /** URL to the System Admin application */
  canAccessSystemAdmin: boolean;
  canRingDrawDeals: boolean;
  canSetUserToAvailable: boolean;
  canViewBusinessMap: boolean;
  canReadWorkflows: boolean;
  destroyed$ = new Subject<boolean>();
  eveningWindowDate: Date;
  morningWindowDate: Date;
  online: boolean = false;
  onlineCheckInterval: any;
  sysAdminUrl: string;

  constructor(
    private sessionService: SessionService,
    private store: Store
  ) {
    this.sysAdminUrl = environment.NOVA_URL;
  }

  ngOnInit() {
    this.store.dispatch(new GoOnlineSubscription(this.user.id) );
    this.canAccessSystemAdmin = this.user.permissions.includes('accessSystemAdmin');
    this.canRingDrawDeals = this.user.permissions.includes('ringDrawDeals');
    this.canViewBusinessMap = this.user.permissions.includes('canViewBusinessMap');
    this.canReadWorkflows = this.user.permissions.includes('workflow.read');
    this.setOnlineParams();
  }

  setOnlineParams() {
    const mountainTimeDate = toZonedTime(new Date(), 'America/Denver'),
      year = mountainTimeDate.getFullYear(),
      month = mountainTimeDate.getMonth(),
      day = mountainTimeDate.getDate();

    this.morningWindowDate = new Date(year, month, day, 6, 0, 0);
    this.eveningWindowDate = new Date(year, month, day, 18, 0, 0);
    this.checkOnlineStatus();
    //check online status every minute to show status display based on time windows
    this.onlineCheckInterval = setInterval(() => this.checkOnlineStatus(), 60000);
    //also subscribe to changes that come from state
    this.onlineStatus$
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe(() => this.checkOnlineStatus());
  }

  checkOnlineStatus() {
    this.onlineStatus$.pipe(take(1))
      .subscribe(onlineStatus => {
        const now = toZonedTime(new Date(), 'America/Denver');

        this.canSetUserToAvailable = this.user.permissions.includes('canSetUserToAvailable') &&
          now > this.morningWindowDate &&
          now < this.eveningWindowDate;

        this.online = onlineStatus && now < this.eveningWindowDate;
      });
  }

  goOnline() {
    this.store.dispatch(new GoOnline(this.user.id));
  }

  goOffline() {
    this.store.dispatch(new GoOffline(this.user.id));
  }

  logout() {
    this.sessionService.logout();
  }

  ngOnDestroy() {
    clearInterval(this.onlineCheckInterval);
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(new GoOfflineSubscription(this.user.id) );
  }
}
